import { axios, transformAxiosError } from "../utils/axios.create";
import { OrganizationLabel } from "../domain/organization-labels.interface";

export type FindAllOrganizationLabelsPayload = {
  search?: string;
  status?: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type OrganizationLabelPayload = {
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE";
  createdBy?: string;
  updatedBy?: string;
};

export type OrganizationLabelsListResponse = {
  list: OrganizationLabel[];
  totalCount: number;
};

const findAll = async ({
  search,
  status,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: FindAllOrganizationLabelsPayload): Promise<OrganizationLabelsListResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (search) {
    queryParams.append("search", search);
  }

  if (status) {
    queryParams.append("status", status);
  }

  return axios
    .get(`/organization-labels?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: body.list || [],
        totalCount: body.totalCount || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<OrganizationLabel> => {
  return axios
    .get(`/organization-labels/find/${id}`)
    .then((res) => {
      return res.data.body as OrganizationLabel;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (
  data: OrganizationLabelPayload
): Promise<OrganizationLabel> => {
  return axios
    .post("/organization-labels", data)
    .then((res) => {
      return res.data.body as OrganizationLabel;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: OrganizationLabelPayload;
}): Promise<OrganizationLabel> => {
  return axios
    .put(`/organization-labels/${id}`, data)
    .then((res) => {
      return res.data.body as OrganizationLabel;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/organization-labels/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const OrganizationLabelsService = {
  findAll,
  findOne,
  create,
  update,
  archive,
};
